import type { BulkNotesInput, NoteColor, NoteDto } from '@keep/shared';
import { useMemo } from 'react';
import { useCurrentUser } from '../../api/auth';
import { useBulkNotes, useCopyNote, useUpdateNote } from '../../api/notes';
import { errorMessage } from '../../lib/api';
import { toast, toastError } from '../../stores/toasts';

const plural = (count: number, one: string, many: string) => (count > 1 ? `${count} ${many}` : one);

/**
 * Actions sur une ou plusieurs notes, partagées par la carte, l'éditeur et la barre de sélection.
 * Chaque action destructive propose « Annuler », comme dans Keep.
 */
export function useNoteActions() {
  const me = useCurrentUser();
  const bulk = useBulkNotes();
  const update = useUpdateNote();
  const copy = useCopyNote();

  // Objet stable : les cartes (mémoïsées) ne se re-rendent pas à chaque rendu de la page.
  const { mutate: mutateBulk } = bulk;
  const { mutate: mutateUpdate } = update;
  const { mutate: mutateCopy } = copy;
  return useMemo(() => {
    const run = (input: BulkNotesInput, message?: string, undo?: BulkNotesInput) =>
      mutateBulk(input, {
        onSuccess: () => {
          if (!message) return;
          toast(
            message,
            undo ? { action: { label: 'Annuler', onClick: () => mutateBulk(undo) } } : {},
          );
        },
        onError: (error) => toastError(errorMessage(error)),
      });

    const ids = (notes: NoteDto[]) => notes.map((n) => n.id);

    return {
      setPinned(notes: NoteDto[], pinned: boolean) {
        run({ action: pinned ? 'pin' : 'unpin', ids: ids(notes) });
      },
      archive(notes: NoteDto[]) {
        const n = notes.length;
        run({ action: 'archive', ids: ids(notes) }, plural(n, 'Note archivée', 'notes archivées'), {
          action: 'unarchive',
          ids: ids(notes),
        });
      },
      unarchive(notes: NoteDto[]) {
        const n = notes.length;
        run(
          { action: 'unarchive', ids: ids(notes) },
          plural(n, 'Note désarchivée', 'notes désarchivées'),
          { action: 'archive', ids: ids(notes) },
        );
      },
      trash(notes: NoteDto[]) {
        const owned = notes.filter((note) => note.owner.id === me.id);
        const shared = notes.filter((note) => note.owner.id !== me.id);
        const message =
          owned.length === 0
            ? plural(shared.length, 'Vous ne collaborez plus à cette note', 'notes quittées')
            : plural(
                notes.length,
                'Note placée dans la corbeille',
                'notes placées dans la corbeille',
              );
        // Quitter une note partagée n'est pas réversible ici : l'annulation ne concerne que les notes possédées.
        run(
          { action: 'trash', ids: ids(notes) },
          message,
          owned.length ? { action: 'restore', ids: ids(owned) } : undefined,
        );
      },
      restore(notes: NoteDto[]) {
        run(
          { action: 'restore', ids: ids(notes) },
          plural(notes.length, 'Note restaurée', 'notes restaurées'),
          { action: 'trash', ids: ids(notes) },
        );
      },
      deleteForever(notes: NoteDto[]) {
        run(
          { action: 'delete', ids: ids(notes) },
          plural(notes.length, 'Note supprimée définitivement', 'notes supprimées définitivement'),
        );
      },
      setColor(notes: NoteDto[], color: NoteColor) {
        run({ action: 'color', ids: ids(notes), color });
      },
      setLabel(notes: NoteDto[], labelId: string, enabled: boolean) {
        run({ action: enabled ? 'addLabel' : 'removeLabel', ids: ids(notes), labelId });
      },
      setReminder(note: NoteDto, reminderAt: string | null) {
        mutateUpdate(
          { id: note.id, patch: { reminderAt } },
          { onError: (error) => toastError(errorMessage(error)) },
        );
      },
      copy(notes: NoteDto[]) {
        for (const note of notes) {
          mutateCopy(note.id, {
            onSuccess: () => toast('Copie créée'),
            onError: (error) => toastError(errorMessage(error)),
          });
        }
      },
    };
  }, [me.id, mutateBulk, mutateUpdate, mutateCopy]);
}

export type NoteActions = ReturnType<typeof useNoteActions>;
